/** Shared bits for number pages: hotel multi-select, variance cells, tiles. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { HotelSummary } from "@owners/shared-types";
import { money, num, pct, pts, valuePct } from "@owners/format";
import { api } from "../api";

export function useHotels() {
  return useQuery({ queryKey: ["hotels"], queryFn: () => api<{ hotels: HotelSummary[] }>("/api/hotels") });
}

/**
 * Multi-select with an "All N" option (SPEC §3 / §10 / §11). `value` is the
 * list of picked hotel ids; an empty list means all.
 */
export function HotelPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const hotels = useHotels().data?.hotels ?? [];
  const [open, setOpen] = useState(false);
  // Unticking "All" clears every box while the menu is open (an empty selection
  // means all, so it can't be stored); the next tick picks just that hotel.
  // Closing the menu with nothing ticked leaves all hotels selected.
  const [cleared, setCleared] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setCleared(false); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const all = value.length === 0 || value.length === hotels.length;
  const label = all ? `All ${hotels.length} hotels` : value.length === 1 ? hotels.find((h) => h.id === value[0])?.name ?? "1 hotel" : `${value.length} hotels`;
  const toggle = (id: string) => {
    if (cleared) { setCleared(false); onChange([id]); return; }
    const cur = all ? hotels.map((h) => h.id) : value;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    onChange(next.length === hotels.length || next.length === 0 ? [] : next);
  };
  const toggleAll = () => {
    if (all && !cleared) { setCleared(true); return; }
    setCleared(false); onChange([]);
  };
  const only = (id: string) => { setCleared(false); onChange(hotels.length === 1 ? [] : [id]); };
  return (
    <div className="hotel-picker" ref={ref}>
      {/* Demo: a gold-outlined box, "HOTEL" over the selection. */}
      <button type="button" className="picker-box" onClick={() => { setOpen(!open); setCleared(false); }} aria-expanded={open}>
        <span className="picker-label">{value.length === 1 && !all ? "Hotel" : "Hotels"}</span>
        <span className="picker-value">{label}</span>
        <span className="picker-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="hotel-picker-menu">
          <label><input type="checkbox" checked={all && !cleared} onChange={toggleAll} /> <strong>All {hotels.length}</strong></label>
          {cleared && <div className="muted" style={{ fontSize: 12, padding: "2px 0 4px" }}>Tick the hotels you want.</div>}
          {hotels.map((h) => (
            <label key={h.id} className="hotel-picker-row">
              <input type="checkbox" checked={!cleared && (all || value.includes(h.id))} onChange={() => toggle(h.id)} /> {h.name}
              <button type="button" className="btn link hotel-picker-only" onClick={(e) => { e.preventDefault(); only(h.id); }}>only</button>
            </label>
          ))}
          {!all && <button type="button" className="btn link" onClick={() => onChange([])}>Select all</button>}
        </div>
      )}
    </div>
  );
}

export const hotelParam = (ids: string[]) => (ids.length ? ids.join(",") : "all");

/** Green when better, red when worse (SPEC §0). `better` null = neutral. */
export function Tone({ better, children }: { better: boolean | null; children: ReactNode }) {
  return <span className={better === null ? "" : better ? "good" : "bad"}>{children}</span>;
}

/** "+$7,200 · +2.8%" vs a base, colored. For costs pass cost=true (under = green). */
export function MoneyVar({ actual, base, cost = false }: { actual: number | null; base: number | null; cost?: boolean }) {
  if (actual == null || base == null) return <span className="muted">—</span>;
  const d = actual - base;
  const p = base === 0 ? null : (d / Math.abs(base)) * 100;
  const better = Math.round(d) === 0 ? null : cost ? d < 0 : d > 0;
  return <Tone better={better}>{valuePct(d, p, { money: true, signed: true })}</Tone>;
}

/** Occupancy change in points: "+2.6 pts". */
export function PtsVar({ actual, base }: { actual: number | null; base: number | null }) {
  if (actual == null || base == null) return <span className="muted">—</span>;
  const d = (actual - base) * 100;
  return <Tone better={Math.abs(d) < 0.05 ? null : d > 0}>{pts(d)}</Tone>;
}

export function Tile({ label, value, sub, onClick, tbd }: { label: string; value: ReactNode; sub?: ReactNode; onClick?: () => void; tbd?: boolean }) {
  return (
    <div className={`card tile${onClick ? " clickable" : ""}`} onClick={onClick} role={onClick ? "button" : undefined}>
      <div className="eyebrow">{label}</div>
      <div className="tile-value">{tbd ? <span className="tbd">TBD</span> : value}</div>
      {sub && <div className="tile-sub">{sub}</div>}
    </div>
  );
}

export { money, num, pct };
